import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import { getCart, removeFromCart, updateCartItemQuantity, clearCart, getCartTotal, getCartTotalWeight } from './services/cartService';
import { getShippingFees, getProducts } from './services/firebaseService';

const Cart = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [shippingFeesLoading, setShippingFeesLoading] = useState(false);
  const [shippingFeesConfig, setShippingFeesConfig] = useState({
    westMalaysia: { firstKg: 10, additionalKg: 3 },
    eastMalaysia: { firstKg: 10, additionalKg: 12 }
  });

  const loadCart = useCallback(async () => {
    setLoading(true);
    let cart = getCart();

    // Auto-remove items the admin has disabled or deleted since they were added
    if (cart.length > 0) {
      const result = await getProducts();
      if (result.success) {
        const productsBySku = {};
        result.products.forEach((p) => {
          if (p.sku) productsBySku[p.sku] = p;
        });
        const removed = cart.filter((item) => {
          const match = productsBySku[item.sku];
          return !match || match.status === 'disable';
        });
        if (removed.length > 0) {
          removed.forEach((item) => removeFromCart(item.sku));
          cart = getCart();
          const names = removed.map((item) => item.title || item.sku).join(', ');
          alert(t('cart.itemsRemovedUnavailable', { products: names }));
        }
      }
    }

    setCartItems(cart);
    setLoading(false);
  }, [t]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const openShippingPolicyModal = async () => {
    setShippingFeesLoading(true);
    const result = await getShippingFees();
    if (result.success) {
      setShippingFeesConfig(result.shippingFees);
    }
    setShippingFeesLoading(false);
    setShowShippingModal(true);
  };

  const formatPrice = (price) => {
    if (!price) return '';
    const numPrice = parseFloat(price.toString().replace(/[^\d.]/g, ''));
    if (isNaN(numPrice)) return price;
    return `RM ${numPrice.toFixed(2)}`;
  };

  // Helper function to check if state is in East Malaysia
  const isEastMalaysia = (state) => {
    const eastMalaysiaStates = ['Sabah', 'Sarawak', 'Labuan', 'WP Labuan'];
    return eastMalaysiaStates.includes(state);
  };

  // Calculate shipping fee based on total price, weight, and region
  const calculateShippingFee = (state = null) => {
    const totalWeight = getCartTotalWeight();
    const isEast = state ? isEastMalaysia(state) : false; // Default to West Malaysia if no state
    
    if (isEast) {
      // East Malaysia rates
      if (totalWeight <= 1) {
        return Number(shippingFeesConfig.eastMalaysia.firstKg);
      } else {
        return Number(shippingFeesConfig.eastMalaysia.firstKg) + ((Math.ceil(totalWeight) - 1) * Number(shippingFeesConfig.eastMalaysia.additionalKg));
      }
    } else {
      // West Malaysia rates (default)
      if (totalWeight <= 1) {
        return Number(shippingFeesConfig.westMalaysia.firstKg);
      } else {
        return Number(shippingFeesConfig.westMalaysia.firstKg) + ((Math.ceil(totalWeight) - 1) * Number(shippingFeesConfig.westMalaysia.additionalKg));
      }
    }
  };

  const handleQuantityChange = (sku, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(sku);
    } else {
      const success = updateCartItemQuantity(sku, newQuantity);
      if (success) {
        loadCart();
        alert(t('cart.quantityUpdated'));
      }
    }
  };

  const handleRemoveItem = (sku) => {
    const success = removeFromCart(sku);
    if (success) {
      loadCart();
      alert(t('cart.itemRemoved'));
    }
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      const success = clearCart();
      if (success) {
        loadCart();
        alert(t('cart.cartCleared'));
      }
    }
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty');
      return;
    }
    // Redirect to checkout page with cart items
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex flex-col items-center justify-center">
        <p className="text-gray-600 text-xl">{t('cart.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Sub-header */}
      {/* <div className="fixed top-20 left-0 right-0 bg-gray-50 border-b border-gray-200 py-3 z-40">
        <div className="max-w-7xl mx-auto px-2 md:px-8">
          <div className="flex justify-end space-x-4">
            <button 
              onClick={() => navigate('/check-order-status')}
              className="flex items-center space-x-2 text-primary hover:text-blue-800 font-semibold transition-colors"
            >
              <AssignmentIcon className="!w-5 !h-5" />
              <span>{t('subHeader.checkOrderStatus')}</span>
            </button>
            <button 
              onClick={openShippingPolicyModal}
              className="flex items-center space-x-2 text-primary hover:text-blue-800 font-semibold transition-colors"
            >
              <LocalShippingIcon className="!w-5 !h-5" />
              <span>{t('subHeader.shippingPolicy')}</span>
            </button>
            <button 
              onClick={() => navigate('/products')}
              className="flex items-center space-x-2 text-primary hover:text-blue-800 font-semibold transition-colors"
            >
              <ShoppingCartIcon className="!w-5 !h-5" />
              <span>{t('subHeader.cart')}</span>
            </button>
          </div>
        </div>
      </div> */}

      <div className="flex-1 flex flex-col pt-8">
        <main className="flex-grow w-full px-2 md:px-8 py-10 max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h1 className="text-3xl font-bold text-primary tracking-tight">Shopping Cart</h1>
              <button onClick={() => navigate(-1)} className="text-primary hover:text-blue-800 flex items-center gap-1 transition-colors">
                <ArrowBackIcon className="!w-5 !h-5" />
                {t('cart.continueShopping')}
              </button>
            </div>
            <p className="text-gray-600">Review items before checkout.</p>
          </div>

          {cartItems.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingCartIcon className="!w-24 !h-24 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-600 mb-2">{t('cart.empty')}</h2>
              <p className="text-gray-500 mb-6">{t('cart.emptyDescription')}</p>
              <button
                onClick={() => navigate('/products')}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-900 transition-colors font-semibold"
              >
                {t('cart.continueShopping')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Column: Cart Items */}
              <section className="flex-grow flex flex-col gap-4">
                <div className="hidden md:grid grid-cols-12 gap-2 pb-2 border-b-2 border-gray-300 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <div className="col-span-6">{t('cart.item')}</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-2 text-right">Unit Price</div>
                  <div className="col-span-2 text-right">Total</div>
                </div>

                {cartItems.map((item) => {
                  const unitPrice = parseFloat(item.price.toString().replace(/[^\d.]/g, ''));
                  const itemTotal = unitPrice * item.quantity;

                  return (
                    <div
                      key={item.sku}
                      className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-center p-3 md:p-0 bg-white md:bg-transparent border border-gray-200 md:border-0 rounded md:rounded-none border-b md:border-b border-gray-300 pb-4"
                    >
                      <div className="col-span-1 md:col-span-6 flex gap-4 items-start">
                        <div className="flex flex-col gap-1 pt-1">
                          <h3 className="text-base font-bold text-primary leading-tight">{item.title}</h3>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <span className="bg-gray-200 text-gray-600 text-[10px] px-2 py-1 mb-1 rounded-sm">
                              WEIGHT: {Number(item.weight || 0).toFixed(2)}KG
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.sku)}
                            className="md:hidden flex items-center gap-1 text-red-500 hover:text-red-700 mt-2 text-sm transition-colors"
                          >
                            <DeleteIcon className="!w-4 !h-4" />
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="col-span-1 md:col-span-2 flex justify-between md:justify-center items-center mt-2 md:mt-0">
                        <span className="md:hidden text-xs font-semibold uppercase tracking-wider text-gray-500">Qty</span>
                        <div className="flex items-center border border-gray-300 rounded bg-white">
                          <button
                            onClick={() => handleQuantityChange(item.sku, item.quantity - 1)}
                            className="flex h-11 w-11 items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <RemoveIcon className="!w-5 !h-5" />
                          </button>
                          <span className="w-10 text-center font-semibold text-primary">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.sku, item.quantity + 1)}
                            className="flex h-11 w-11 items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors"
                            aria-label="Increase quantity"
                          >
                            <AddIcon className="!w-5 !h-5" />
                          </button>
                        </div>
                      </div>

                      <div className="col-span-1 md:col-span-2 flex justify-between md:justify-end items-center">
                        <span className="md:hidden text-xs font-semibold uppercase tracking-wider text-gray-500">Unit Price</span>
                        <span className="text-sm text-gray-600">{formatPrice(unitPrice)}</span>
                      </div>

                      <div className="col-span-1 md:col-span-2 flex justify-between md:justify-end items-center relative">
                        <span className="md:hidden text-xs font-semibold uppercase tracking-wider text-gray-500">Total</span>
                        <span className="text-lg font-bold text-primary">{formatPrice(itemTotal)}</span>
                        <button
                          onClick={() => handleRemoveItem(item.sku)}
                          className="hidden md:flex absolute -right-8 text-gray-500 hover:text-red-500 transition-colors p-1"
                          title="Remove Item"
                          aria-label="Remove item"
                        >
                          <CloseIcon className="!w-5 !h-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-2 flex justify-start">
                  <button
                    onClick={handleClearCart}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-primary transition-colors py-2"
                  >
                    <DeleteSweepIcon className="!w-5 !h-5" />
                    {t('cart.clearCart')}
                  </button>
                </div>
              </section>

              {/* Right Column: Order Summary */}
              <aside className="w-full lg:w-[380px] flex-shrink-0">
                <div className="bg-white border border-gray-200 p-6 rounded shadow-sm md:sticky md:top-24">
                  <h2 className="text-xl font-bold text-primary border-b-2 border-gray-300 pb-2 mb-4">
                    Order Summary
                  </h2>
                  <div className="flex flex-col gap-3 text-sm text-gray-700 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Subtotal ({cartItems.length} items)</span>
                      <span className="font-bold">{formatPrice(getCartTotal())}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Total Weight</span>
                      <span>{getCartTotalWeight().toFixed(2)} kg</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 flex items-center gap-1">
                        Shipping Fees
                        <button
                          type="button"
                          onClick={openShippingPolicyModal}
                          className="text-primary hover:text-blue-900 transition-colors"
                          aria-label="View shipping policy"
                        >
                          <InfoOutlinedIcon className="!w-4 !h-4" />
                        </button>
                      </span>
                      <span>{calculateShippingFee() === 0 ? 'FREE' : formatPrice(calculateShippingFee())}</span>
                    </div>
                    
                  </div>

                  <div className="border-t-2 border-primary pt-2 mb-4 flex justify-between items-end">
                    <span className="text-lg font-bold text-primary">Grand Total</span>
                    <span className="text-2xl font-bold text-primary">{formatPrice(getCartTotal() + calculateShippingFee())}</span>
                  </div>

                  <button
                    onClick={handleCheckout}
                    className="w-full bg-primary hover:bg-blue-900 text-white font-bold py-3 px-6 rounded flex items-center justify-center gap-2 transition-colors duration-200 active:opacity-90"
                  >
                    <LockIcon className="!w-5 !h-5" />
                    {t('cart.checkout')}
                  </button>
                  
                </div>
              </aside>
            </div>
          )}
        </main>
      </div>

      {/* Shipping Policy Modal */}
      {showShippingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowShippingModal(false)}>
          <div className="bg-white rounded-2xl p-4 md:p-8 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary">{t('shipping.title')}</h2>
              <button 
                onClick={() => setShowShippingModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {shippingFeesLoading && (
                <p className="text-gray-500 text-sm">Refreshing latest shipping fees...</p>
              )}
              {/* West Malaysia Rates */}
              <div>
                <h3 className="text-lg font-semibold text-primary mb-3">West Malaysia</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">Standard Delivery</h4>
                  <p className="text-blue-700 text-sm">First 1kg: RM{Number(shippingFeesConfig.westMalaysia.firstKg).toFixed(2)}</p>
                  <p className="text-blue-700 text-sm">Additional 1kg: RM{Number(shippingFeesConfig.westMalaysia.additionalKg).toFixed(2)}</p>
                </div>
              </div>

              {/* East Malaysia Rates */}
              <div>
                <h3 className="text-lg font-semibold text-primary mb-3">East Malaysia (Sabah, Sarawak, Labuan)</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-blue-800 mb-2">Standard Delivery</h4>
                  <p className="text-blue-700 text-sm">First 1kg: RM{Number(shippingFeesConfig.eastMalaysia.firstKg).toFixed(2)}</p>
                  <p className="text-blue-700 text-sm">Additional 1kg: RM{Number(shippingFeesConfig.eastMalaysia.additionalKg).toFixed(2)}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <button 
                onClick={() => setShowShippingModal(false)}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-semibold"
              >
                {t('shipping.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
